/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { memo } from "react";
import type { FunctionComponent, JSX, KeyboardEvent } from "react";

import { selectIsTvDevice } from "../../../viewModel/device";
import type { ListItemProps } from "./ListItemProps";

import "./List.scss";

/** `KeyboardEvent.key` values that activate an interactive row. */
const KEY_ENTER: string = "Enter";
const KEY_SPACE: string = " ";

const ListItemComponent: FunctionComponent<ListItemProps> = (
  props: ListItemProps
): JSX.Element => {
  const {
    children,
    index,
    isFocused,
    onFocus,
    onClick,
    isPresentational = false,
    className
  } = props;

  const handleFocus = (): void => {
    if (onFocus !== undefined && onFocus !== null) {
      onFocus(index);
    }
  };

  /**
   * Pointer-driven focus — browser only, and reported on *movement* rather than
   * on `mouseenter`.
   *
   * `mouseenter` fires whenever the element under the pointer changes, including
   * when the pointer never moved and the list scrolled beneath it. D-pad focus
   * scrolls the focused row into view, which slides a different row under a
   * resting desktop cursor, and that phantom enter reported focus for *that*
   * row: walking down at repeat speed, the ring kept snapping back to whichever
   * row had drifted under the mouse instead of following the keys. `mousemove`
   * only fires for real pointer travel, so a scroll can no longer steal focus.
   *
   * TV platforms are excluded outright — there is no cursor to hover with, and a
   * remote that parks a ghost pointer over the stack would otherwise fight the
   * D-pad for the same reason. The `isFocused` guard keeps it to one report per
   * row: the event repeats for every pixel of travel.
   *
   * The same treatment `ContentCard` gives its own surface — see
   * `handlePointerMove` there.
   */
  const handlePointerMove = (): void => {
    if (selectIsTvDevice()) {
      return;
    }
    if (isFocused === true) {
      return;
    }
    handleFocus();
  };

  const handleClick = (): void => {
    if (onClick !== undefined && onClick !== null) {
      onClick(index);
    }
  };

  const rootClassName: string = `list__item ${
    isFocused ? "list__item--focused" : ""
  } ${isPresentational ? "list__item--presentational" : ""} ${className || ""}`
    .replace(/\s+/g, " ")
    .trim();

  // A presentational wrapper carries no role, no tab stop and no handlers: the
  // row it wraps is already interactive and owns those itself.
  if (isPresentational) {
    return <div className={rootClassName}>{children}</div>;
  }

  return (
    <div
      className={rootClassName}
      tabIndex={0}
      role="button"
      onFocus={handleFocus}
      onMouseMove={handlePointerMove}
      onClick={handleClick}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === KEY_ENTER || event.key === KEY_SPACE) {
          // Space would otherwise scroll the page.
          event.preventDefault();
          handleClick();
        }
      }}
    >
      {children}
    </div>
  );
};

export const ListItem = memo(ListItemComponent);
